import React, { useEffect, useMemo, useRef } from 'react';
import { SearchProductCardWrapper } from './SearchProductCardWrapper';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { LoadingSkeleton } from '@jbhi-fi/components/src/LoadingSkeleton/LoadingSkeleton';
import { trackViewItemList } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackViewItemList';
import { getStoreCurrency } from '@jbhi-fi/utils/src/shopify/getStoreCurrency';
import { transformAlgoliaHitForTracking } from '@jbhi-fi/components/src/Algolia/transformers/transformAlgoliaHitForTracking';
import { toCloudRetailProductDetail } from '@jbhi-fi/utils/src/ga4Tracking/functions/toCloudRetailProductDetail';
import { useGoogleRecentlyViewed } from '../../hooks/useGoogleSearch';

const PRODUCTS_LIMIT = 4;

const GoogleRecentlyViewedResults = ({ storedProducts }: { storedProducts: Array<number> }) => {
  const { hits, attributionToken, isLoading } = useGoogleRecentlyViewed(storedProducts);
  const isDesktop = useBreakpoint('md', 'up');

  const sortedHits = useMemo(
    () =>
      [...hits]
        .sort((a, b) => {
          const skuA = a.sku || a.skus?.[0];
          const skuB = b.sku || b.skus?.[0];
          return storedProducts.indexOf(Number(skuA)) - storedProducts.indexOf(Number(skuB));
        })
        .slice(0, PRODUCTS_LIMIT),
    [hits, storedProducts]
  );

  const hitsKey = sortedHits.map(h => h.objectID).join(',');
  const prevHitsKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (sortedHits.length === 0) return;
    if (hitsKey === prevHitsKeyRef.current) return;
    prevHitsKeyRef.current = hitsKey;

    trackViewItemList({
      currency: getStoreCurrency(),
      listName: 'search - recently viewed',
      searchType: 'suggested search',
      viewedItems: transformAlgoliaHitForTracking(sortedHits),
      cloudRetail: {
        attributionToken,
        productDetails: sortedHits.map(toCloudRetailProductDetail),
        filter: null,
      },
    });
  }, [hitsKey, attributionToken, sortedHits]);

  if (storedProducts.length <= 0) return null;

  if (isLoading) {
    return (
      <>
        {[...Array(PRODUCTS_LIMIT)].map((_, i) => (
          <LoadingSkeleton.ProductCardSkeleton shouldShimmer variant="small" key={i} />
        ))}
      </>
    );
  }

  if (hits.length === 0) return <div>No results</div>;

  const productCards = sortedHits.map((hit, index) => (
    <SearchProductCardWrapper type="modal" hit={hit} key={hit.variant_id} position={index + 1} variant="small" />
  ));

  return (
    <>
      {isDesktop ? (
        productCards
      ) : (
        <Carousel showPagination={false} slidesPerView="auto" fullBleed>
          {productCards}
        </Carousel>
      )}
    </>
  );
};

export default GoogleRecentlyViewedResults;
